import { sendAnalytics } from '@audio-network/analytics'
import { Container } from '@audio-network/componentlibrary'
import { type GetServerSideProps } from 'next'
import Head from 'next/head'
import singletonRouter from 'next/router'
import { useTranslation } from 'next-i18next'
import { useEffect, useMemo } from 'react'
import { Configure, ConfigureProps, InstantSearch } from 'react-instantsearch'
import { createInstantSearchRouterNext } from 'react-instantsearch-router-nextjs'

import {
	AlgoliaInsightsMiddleware,
	BlockResults,
	CurrentRefinements,
	DownloadAlert,
	Filters,
	Results,
	SearchBox,
	SendAnalytics,
	SortBy,
} from '@/components/pages/search'
import AddToFavouritesSpotlight from '@/components/pages/search/AddToFavouritesSpotlight/AddToFavouritesSpotlight'
import SyncAiSearchToRouter from '@/components/pages/search/SyncAISearchToRouter/SyncAiSearchToRouter'
import TrackInfoSpotlight from '@/components/pages/search/TrackInfoSpotlight/TrackInfoSpotlight'
import {
	AlgoliaSearchKey,
	issueAlgoliaSearchKey,
} from '@/pages/api/musicapp/_services/algolia/searchKey'
import { getCatalogues } from '@/pages/api/musicapp/_services/catalogueService'
import { getTracks } from '@/pages/api/musicapp/_services/musicService'
import getPlaylist from '@/pages/api/musicapp/_services/playlistService'
import { getCurrentSession } from '@/pages/api/musicapp/_services/sessionService'
import useAiSearchStore from '@/stores/aiSearchStore'
import useSearchSelectorStore, {
	isPromptSearchMode,
	isReferenceSearchMode,
} from '@/stores/searchSelectorStore'
import { createSearchClient } from '@/utils/algolia/client'
import formatTitleByLocale from '@/utils/formatting/formatTitleByLocale'
import { getIpAddressFromHeader } from '@/utils/getIpAddressFromHeader'
import { withGlobalTranslations } from '@/utils/i18n/withGlobalTranslations'

import {
	createSearchUrlWithCustomParams,
	parseSearchUrlWithCustomParams,
} from './algoliaRoutingHelpers'
import { getAttributesToRetrieve } from './attributesToRetrieveHelper'
import styles from './index.module.css'
import {
	getLyricsRestrictedAttributes,
	getTrackRestrictedAttributes,
} from './restrictedTrackAttributesHelper'

type SearchKeywordPageProps = {
	locale: string
	playlistTracks: Track[]
	user: User
	viewableCatalogues: number[]
	algoliaSearchKey: AlgoliaSearchKey
}

export default function Search({
	locale,
	playlistTracks,
	user,
	viewableCatalogues,
	algoliaSearchKey,
}: SearchKeywordPageProps): JSX.Element {
	const { t } = useTranslation(['search'])
	const trackIndex = `${process.env.NEXT_PUBLIC_ALGOLIA_INDEX}`

	const lyricsRestrictionCommitted = useSearchSelectorStore(
		(state) => state.lyricsRestrictionCommitted
	)

	const searchClient = useMemo(
		() => createSearchClient(algoliaSearchKey.apiKey),
		[algoliaSearchKey.apiKey]
	)

	const searchType = useSearchSelectorStore((state) => state.searchType)
	const catalogueFilters = `Album.Catalogue.Id = ${viewableCatalogues.join(
		' OR Album.Catalogue.Id = '
	)}`

	const { aiSearchFilters } = useAiSearchStore()

	const configureProps = {
		filters: aiSearchFilters
			? `${catalogueFilters} AND ${aiSearchFilters}`
			: `${catalogueFilters}`,
		hitsPerPage: 100,
		restrictSearchableAttributes: lyricsRestrictionCommitted
			? [...getLyricsRestrictedAttributes()]
			: getTrackRestrictedAttributes(locale),
		attributesToRetrieve: getAttributesToRetrieve(),
		attributesToHighlight: [], // disable highlight
		attributesToSnippet: [], // disable snippets
	} as ConfigureProps

	useEffect(() => {
		sendAnalytics({
			noun: 'Search Page',
			pastTenseVerb: 'Viewed',
		})
	}, [])

	const hideResults =
		!user &&
		(isPromptSearchMode(searchType) || isReferenceSearchMode(searchType))

	return (
		<>
			<Head>
				<title>{formatTitleByLocale(locale, t('title'))}</title>
			</Head>
			<InstantSearch
				// https://www.algolia.com/doc/guides/building-search-ui/widgets/show-and-hide-widgets/react#enable-preservesharedstateonunmount
				future={{ preserveSharedStateOnUnmount: true }}
				searchClient={searchClient}
				indexName={trackIndex}
				routing={{
					router: createInstantSearchRouterNext({
						singletonRouter,
						routerOptions: {
							cleanUrlOnDispose: false,
							createURL: ({ qsModule, location, routeState }) => {
								// runs whenever InstantSearch builds a URL.
								return createSearchUrlWithCustomParams({
									qsModule,
									location,
									routeState,
									user,
								})
							},
							parseURL: ({ qsModule, location }) => {
								// runs whenever InstantSearch parses a URL.
								return parseSearchUrlWithCustomParams({
									qsModule,
									location,
									user,
								})
							},
						},
					}),
				}}
			>
				<SyncAiSearchToRouter router={singletonRouter} user={user} />
				<SendAnalytics />
				<Configure {...configureProps} />
				<div className={styles.header}>
					<DownloadAlert user={user} />
					<SearchBox searchClient={searchClient} user={user} />
					<Filters locale={locale} />
					<Container className={styles.refinements} maxWidth="large">
						<CurrentRefinements />
						<SortBy />
					</Container>
				</div>
				{hideResults ? (
					<BlockResults searchType={searchType} />
				) : (
					<Results
						locale={locale}
						playlistTracks={playlistTracks}
						user={user}
					/>
				)}
				<AlgoliaInsightsMiddleware user={user} />
			</InstantSearch>
			<AddToFavouritesSpotlight user={user} />
			<TrackInfoSpotlight user={user} />
		</>
	)
}

export const getServerSideProps: GetServerSideProps = async ({
	locale,
	res,
	req,
}) => {
	const trackIds = (await getPlaylist('new-releases', locale))?.trackIds.split(
		','
	)

	const playlistTracks = trackIds ? await getTracks(trackIds) : []

	const ipAddress = getIpAddressFromHeader(
		req.headers,
		req.socket.remoteAddress
	)

	const user: User = await getCurrentSession(req, res)

	const viewableCatalogues = await getCatalogues(ipAddress)

	const algoliaSearchKey = issueAlgoliaSearchKey()

	return {
		props: {
			...(await withGlobalTranslations(locale, [
				'search',
				'trackrow',
				'spotlights',
			])),
			locale,
			playlistTracks,
			user,
			viewableCatalogues,
			algoliaSearchKey,
		},
	}
}
